import { isDarkModeAtom, themeAtom } from "../../../shared/atoms";
import StarIcon from "../StarIcon";
import { useAtomValue } from "jotai";
import React, { useEffect, useState, useMemo } from "react";
import { twMerge } from "tailwind-merge";

interface Props {
  stars: number;
  error?: string;
  onClick?: (starIndex: number) => void;
  showStarCount?: boolean;
  starFillColor?: string;
  starSize?: number;
  starGap?: string;
  readonly?: boolean;
}

export function useStarColors() {
  const isDark = useAtomValue(isDarkModeAtom);

  return {
    PRIMARY_STAR_FILL: isDark ? "#DF7C00" : "#FFB100",
    INACTIVE_STAR_FILL: isDark ? "#94A3B8" : "#9989A6",
    INACTIVE_STAR_FILL_READONLY: isDark ? "#80A0A0" : "#DFE4E2",
    ERROR_STAR_FILL: "#BF0002",
  };
}

const StarRating: React.FC<Props> = ({
  stars,
  error,
  onClick,
  showStarCount = false,
  starFillColor,
  starSize,
  starGap,
  readonly = false,
}) => {
  const theme = useAtomValue(themeAtom);

  const { PRIMARY_STAR_FILL, INACTIVE_STAR_FILL, INACTIVE_STAR_FILL_READONLY, ERROR_STAR_FILL } = useStarColors();

  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [activeStars, setActiveStars] = useState<number>(stars);
  const gap = starGap ?? "gap-4";

  useEffect(() => {
    setActiveStars(stars);
  }, [stars]);

  const handleMouseEnter = (starIndex: number) => {
    if (readonly) return;
    setActiveStars(stars);
    setHoveredStar(starIndex);
  };

  const handleMouseLeave = () => {
    if (readonly) return;
    setActiveStars(stars);
    setHoveredStar(null);
  };

  const handleStarClick = (starIndex: number) => {
    if (readonly) return;
    onClick?.(starIndex + 1);
    setActiveStars(starIndex + 1);
  };

  const ACTIVE_COLOR = useMemo(() => starFillColor ?? PRIMARY_STAR_FILL, [starFillColor, PRIMARY_STAR_FILL]);

  const getFillColor = (starIndex: number): string => {
    if (starIndex + 1 <= activeStars) {
      return ACTIVE_COLOR;
    } else if (hoveredStar !== null && starIndex <= hoveredStar) {
      return ACTIVE_COLOR;
    } else if (error) {
      return ERROR_STAR_FILL;
    } else if (readonly) {
      return INACTIVE_STAR_FILL_READONLY;
    } else {
      return INACTIVE_STAR_FILL;
    }
  };

  return (
    <>
      <div className="relative flex flex-wrap items-center">
        <div
          data-test="star-rating"
          className={twMerge("flex", !readonly && "cursor-pointer", gap)}
          onMouseLeave={handleMouseLeave}
        >
          {Array.from({ length: 5 }).map((_, starIndex) => {
            const color = getFillColor(starIndex);
            return (
              <span
                key={starIndex}
                data-test={`star-${starIndex + 1}`}
                onMouseEnter={() => handleMouseEnter(starIndex)}
                onClick={() => handleStarClick(starIndex)}
              >
                <StarIcon
                  filled={color !== INACTIVE_STAR_FILL}
                  error={error}
                  fillColor={color}
                  emptyColor={INACTIVE_STAR_FILL}
                  starSize={starSize}
                />
              </span>
            );
          })}
        </div>

        {showStarCount && <p className={twMerge("ml-4 text-sm", theme.vesselDetails.infoText)}>{`${activeStars}/5`}</p>}
      </div>

      {error && <p className={twMerge("mt-1 text-xs", theme.crtDropdown.errorText)}>{error}</p>}
    </>
  );
};

export default StarRating;
